import React, { useState } from 'react';
import { Box, Typography, Grid, TextField, Button, Paper, Snackbar, Alert, Container, useTheme } from '@mui/material';
import { useTranslation } from '../hooks/useTranslation';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import EmailIcon from '@mui/icons-material/Email';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { TikTokIcon } from '../components/icons/TikTokIcon';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  height: '100%',
  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px) scale(1.02)',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)',
  },
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
  borderRadius: '20px',
  border: '1px solid rgba(0, 0, 0, 0.05)',
}));

const GradientSection = styled(Box)(({ theme }) => ({
  borderRadius: '30px',
  padding: theme.spacing(6),
  marginBottom: theme.spacing(6),
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
  },
}));

const SocialButton = styled(Button)<{ 
  component?: React.ElementType;
  href?: string;
  target?: string;
  rel?: string;
}>(({ theme }) => ({
  background: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  '&:hover': {
    background: theme.palette.primary.dark,
    transform: 'scale(1.05)',
  },
  transition: 'all 0.3s ease-in-out',
  margin: theme.spacing(1),
  borderRadius: '30px',
  padding: theme.spacing(1, 2.5),
}));

const AnimatedContainer = motion(Container);

export const ContactPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isRTL = theme.direction === 'rtl';
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch('http://localhost:3001/api/email/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: 'inbal.artmakers@gmail.com',
          subject: formData.subject,
          text: `Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send email');
      }

      setSnackbar({
        open: true,
        message: t('pages.contact.form.successMessage'),
        severity: 'success',
      });
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Error sending email:', error);
      setSnackbar({
        open: true,
        message: t('pages.contact.form.errorMessage'),
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const formatText = (text: string) => {
    if (!isRTL) return text;
    return text.replace(/([.!?])(\s|$)/g, '$2$1');
  };

  return (
    <AnimatedContainer 
      maxWidth="lg" 
      sx={{ py: 6 }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <Typography 
          variant="h3" 
          gutterBottom 
          sx={{ 
            textAlign: 'center',
            fontWeight: 700,
            background: theme.palette.primary.main,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 4,
          }}
        >
          {formatText(t('pages.contact.title'))}
        </Typography>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <GradientSection>
          <Typography 
            variant="body1" 
            sx={{ 
              maxWidth: '800px',
              mx: 'auto',
              textAlign: 'center',
              lineHeight: 1.8,
              direction: isRTL ? 'rtl' : 'ltr',
              unicodeBidi: 'embed',
              mb: 4
            }}
          >
            {formatText(t('pages.contact.subtitle'))}
          </Typography>
          <Box sx={{ textAlign: 'center' }}>
            <SocialButton 
              startIcon={<EmailIcon />}
              component="a"
              href="mailto:inbal.artmakers@gmail.com"
            >
              {t('pages.contact.email')}
            </SocialButton>
            <SocialButton 
              startIcon={<InstagramIcon />}
              component="a"
              href="https://www.instagram.com/inbal.artmakers/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Instagram
            </SocialButton>
            <SocialButton 
              startIcon={<FacebookIcon />}
              component="a"
              href="https://www.facebook.com/inbalijewelry"
              target="_blank"
              rel="noopener noreferrer"
            >
              Facebook
            </SocialButton>
            <SocialButton 
              startIcon={<WhatsAppIcon />}
              component="a"
              href="https://wa.me/message/RT4MXN3435UGG1"
              target="_blank"
              rel="noopener noreferrer"
            >
              WhatsApp
            </SocialButton>
            <SocialButton 
              startIcon={<TikTokIcon />}
              component="a"
              href="https://www.tiktok.com/@inbal.artmakers?_t=ZS-8vbx2aglBkG&_r=1"
              target="_blank"
              rel="noopener noreferrer"
            >
              TikTok
            </SocialButton>
          </Box>
        </GradientSection>
      </motion.div>

      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <StyledPaper>
              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label={t('pages.contact.form.name')}
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  label={t('pages.contact.form.email')}
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  label={t('pages.contact.form.subject')}
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  label={t('pages.contact.form.message')}
                  name="message"
                  multiline
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  margin="normal"
                  required
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  sx={{ 
                    mt: 2,
                    background: theme.palette.primary.main,
                    '&:hover': {
                      background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                    }
                  }}
                >
                  {t('pages.contact.form.submit')}
                </Button>
              </form>
            </StyledPaper>
          </motion.div>
        </Grid>

        <Grid item xs={12} md={6}>
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.8 }}
          >
            <StyledPaper>
              <Typography 
                variant="h6" 
                gutterBottom 
                sx={{ 
                  fontWeight: 600,
                  color: 'primary.main',
                  mb: 3,
                  textAlign: isRTL ? 'right' : 'left'
                }}
              >
                {t('pages.contact.info.title')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      fontWeight: 600,
                      color: 'text.secondary',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  >
                    {t('pages.contact.info.addressTitle')}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ textAlign: isRTL ? 'right' : 'left' }}
                  >
                    {t('pages.contact.info.address')}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      fontWeight: 600,
                      color: 'text.secondary',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  >
                    {t('pages.contact.info.phoneTitle')}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ textAlign: isRTL ? 'right' : 'left' }}
                  >
                    {t('pages.contact.info.phone')}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      fontWeight: 600,
                      color: 'text.secondary',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  >
                    {t('pages.contact.info.emailTitle')}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ textAlign: isRTL ? 'right' : 'left' }}
                  >
                    {t('pages.contact.info.email')}
                  </Typography>
                </Grid>
              </Grid>
            </StyledPaper>
          </motion.div>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AnimatedContainer>
  );
}; 